import { useEffect, useMemo, useState } from 'react';

import DateFnsUtils from '@date-io/date-fns';
import {
  alpha,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  ListItem,
  ListItemAvatar,
  ListItemText,
  makeStyles,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
  TimePicker,
} from '@material-ui/pickers';
import { ptBR } from 'date-fns/locale';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useModal } from 'react-modal-hook';

import { BlockedContactName } from 'components/BlockedContactName';
import DialogForm from 'components/DialogForm';
import DialogHeader from 'components/DialogHeader';
import ModalListaContatos from 'components/ModalListaContatos';
import useCurrentUser from 'hooks/useCurrentUser';
import dayjsService from 'services/dayjs';
import { useAppDispatch, useAppSelector } from 'store';
import {
  criarAgendamento,
  fetchAgendamentosV2,
  resetAgendamentosV2,
} from 'store/reducers/agendamentos';
import {
  canaisSelector,
  fetchCanaisSelect,
  resetCanaisSelect,
  TipoCanalEnum,
} from 'store/reducers/canais';
import { ContatoInterface } from 'store/reducers/contatos';
import {
  fetchMinhasEquipes,
  resetMinhasEquipes,
  usuariosSelector,
} from 'store/reducers/usuarios';
import getIconTipoCanal from 'utils/getIconTipoCanal';
import * as validators from 'utils/validators';

const useStyles = makeStyles(() => ({
  truncateValue: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    display: 'flex',
    gap: 8,
  },
  truncateText: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  menuItem: {
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
}));

type ModalAddAgendamentoProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  contato?: ContatoInterface;
};

type FormValues = {
  canalId: string;
  equipeId: string;
  agendarParaDate: string | Date | null;
  agendarParaTime: string | Date | null;
  mensagem: string;
  comentario: string | null;
};

function ModalAddAgendamento(props: ModalAddAgendamentoProps) {
  const { open, onClose, onSuccess, contato } = props;
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useCurrentUser();
  const classes = useStyles();
  const [contatoSelected, setContatoSelected] = useState<
    ContatoInterface | undefined
  >();

  const dispatch = useAppDispatch();
  const { minhasEquipes: equipes } = useAppSelector(usuariosSelector);
  const { canaisSelect } = useAppSelector(canaisSelector);

  const canaisFiltrados = useMemo(
    () => canaisSelect.filter(c => c.type === TipoCanalEnum.WHATSAPP) || [],
    [canaisSelect],
  );

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      canalId: '',
      equipeId: equipes.length === 1 ? equipes[0]?.id : '',
      agendarParaDate: null,
      agendarParaTime: null,
      mensagem: '',
      comentario: '',
    },
    validationSchema: validators.criarAgendamentoSchema,
    onSubmit: async values => {
      const data = dayjsService(values.agendarParaDate).format('YYYY-MM-DD');
      const hora = dayjsService(values.agendarParaTime).format('HH:mm');

      if (contatoSelected) {
        await dispatch(
          criarAgendamento({
            ...values,
            contatoId: contatoSelected.id,
            agendarParaDate: data,
            agendarParaTime: hora,
          }),
        )
          .unwrap()
          .then(() => {
            if (currentUser) {
              dispatch(resetAgendamentosV2());
              dispatch(
                fetchAgendamentosV2({
                  attendantId: currentUser.id,
                }),
              );
            }

            enqueueSnackbar(`O agendamento foi criado com sucesso.`);

            onSuccess?.();
            onClose();
          })
          .catch(error => {
            if (error.response) enqueueSnackbar(error.response.data.message);
          });
      }
    },
  });

  const handleClose = () => {
    if (!formik.isSubmitting) onClose();
  };

  const handleChangeContato = (newContato: ContatoInterface) => {
    setContatoSelected(newContato);
    formik.setFieldValue('equipeId', '');
    formik.setFieldValue('canalId', '');
  };

  const [showModalListaContatos, hideModalListaContatos] = useModal(
    ({ in: open }) => (
      <ModalListaContatos
        open={open}
        onClose={hideModalListaContatos}
        selected={contatoSelected}
        onSelect={handleChangeContato}
        filterByType={TipoCanalEnum.WHATSAPP}
      />
    ),
    [contatoSelected],
  );

  const theme = useTheme();

  //* formik errors
  const agendarParaDataError =
    formik.touched.agendarParaDate && formik.errors.agendarParaDate;
  const agendarParaHoraError =
    formik.touched.agendarParaTime && formik.errors.agendarParaTime;
  const mensagemError = formik.touched.mensagem && formik.errors.mensagem;
  const comentarioError = formik.touched.comentario && formik.errors.comentario;

  const { setFieldValue, values } = formik;

  const isBlocked = Boolean(contatoSelected?.blockedAt);

  // Busca as equipes apenas quando o contato for selecionado
  useEffect(() => {
    if (open && contatoSelected?.id) {
      dispatch(fetchMinhasEquipes());
    }
  }, [open, contatoSelected?.id, dispatch]);

  // Auto seleciona a equipe caso tenha apenas uma
  useEffect(() => {
    if (!open || !contatoSelected) return;

    const possuiUmaEquipe = equipes.length === 1;
    const naoTemEquipeSelecionada = !values.equipeId;

    if (possuiUmaEquipe && naoTemEquipeSelecionada) {
      setFieldValue('equipeId', equipes[0].id);
    }
  }, [open, contatoSelected, equipes, values.equipeId, setFieldValue]);

  // Busca os canais sempre que a equipe ou o contato mudar
  useEffect(() => {
    if (!open || !contatoSelected?.id) return;

    dispatch(
      fetchCanaisSelect({
        contactId: contatoSelected.id,
        equipeId: values.equipeId || undefined,
      }),
    );
  }, [open, contatoSelected?.id, values.equipeId, dispatch]);

  // Auto seleciona o canal caso tenha apenas um ou um canal padrão
  useEffect(() => {
    if (canaisFiltrados.length === 0) {
      if (values.canalId) {
        setFieldValue('canalId', '');
      }
      return;
    }

    // Só auto-seleciona se ainda não tiver um canal válido selecionado
    const canalAtualValido = canaisFiltrados.some(c => c.id === values.canalId);

    if (!canalAtualValido) {
      if (canaisFiltrados.length === 1) {
        setFieldValue('canalId', canaisFiltrados[0].id);
      } else {
        const defaultCanal = canaisFiltrados.find(c => c.isDefault);
        if (defaultCanal) {
          setFieldValue('canalId', defaultCanal.id);
        }
      }
    }
  }, [canaisFiltrados, setFieldValue, values.canalId]);

  const canalLabel = (() => {
    if (canaisFiltrados.length > 0) {
      return 'Canal';
    }

    if (!values.equipeId) {
      return 'Selecione uma equipe';
    }

    return 'Nenhum canal disponível';
  })();

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={handleClose}
      TransitionProps={{
        onEnter: () => {
          formik.resetForm();
          setContatoSelected(contato);
        },
        onExited: () => {
          formik.resetForm();
          setContatoSelected(undefined);
          dispatch(resetCanaisSelect());
          dispatch(resetMinhasEquipes());
        },
      }}
    >
      <DialogHeader title="Criar agendamento" onClose={handleClose} />

      <DialogForm onSubmit={formik.handleSubmit} loading={formik.isSubmitting}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={12}>
              <ListItem
                button
                selected
                disabled={Boolean(contato)}
                onClick={showModalListaContatos}
                style={{ ...(!contatoSelected && { border: '1px dashed' }) }}
              >
                {contatoSelected && (
                  <ListItemAvatar>
                    <Avatar src={contatoSelected?.foto} />
                  </ListItemAvatar>
                )}

                <ListItemText
                  primary={
                    contatoSelected ? (
                      <BlockedContactName
                        name={contatoSelected.nome}
                        isBlocked={Boolean(contatoSelected.blockedAt)}
                      />
                    ) : (
                      'Clique para selecionar um contato'
                    )
                  }
                  secondary={contatoSelected?.contato}
                  primaryTypographyProps={
                    {
                      noWrap: true,
                      variant: 'body2',
                      component: 'div',
                      ...(!contatoSelected && { align: 'center' }),
                    } as Record<string, unknown>
                  }
                  secondaryTypographyProps={{
                    noWrap: true,
                    variant: 'caption',
                  }}
                />
              </ListItem>
            </Grid>

            <Grid item xs={12} lg={6}>
              <TextField
                select
                fullWidth
                id="equipeId"
                name="equipeId"
                variant="outlined"
                label="Equipe"
                disabled={!contatoSelected || isBlocked || equipes.length <= 0}
                value={formik.values.equipeId}
                onChange={e => {
                  formik.setFieldValue('canalId', '');
                  formik.handleChange(e);
                }}
                error={
                  formik.touched.equipeId && Boolean(formik.errors.equipeId)
                }
                helperText={formik.touched.equipeId && formik.errors.equipeId}
              >
                {equipes.map(equipe => (
                  <MenuItem key={equipe.id} value={equipe.id}>
                    <Typography variant="body2">{equipe.nome}</Typography>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} lg={6}>
              <TextField
                select
                fullWidth
                id="canalId"
                name="canalId"
                variant="outlined"
                label={canalLabel}
                disabled={
                  !contatoSelected ||
                  isBlocked ||
                  canaisFiltrados.length <= 0 ||
                  !formik.values.equipeId
                }
                value={formik.values.canalId}
                onChange={formik.handleChange}
                error={formik.touched.canalId && Boolean(formik.errors.canalId)}
                helperText={formik.touched.canalId && formik.errors.canalId}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: selected => {
                    if ((selected as string) === '') {
                      return null;
                    }
                    const canal = canaisFiltrados.find(c => c.id === selected);
                    return canal ? (
                      <Box className={classes.truncateValue}>
                        <span style={{ flexShrink: 0 }}>
                          {getIconTipoCanal({
                            tipoCanal: canal.type,
                            size: 16,
                            isModoHibrido: canal.isHybridMode,
                          })}
                        </span>
                        <span className={classes.truncateText}>
                          {canal.name}
                        </span>
                      </Box>
                    ) : (
                      selected
                    );
                  },
                }}
              >
                {canaisFiltrados.map(canal => (
                  <MenuItem
                    key={canal.id}
                    value={canal.id}
                    className={classes.menuItem}
                  >
                    {getIconTipoCanal({
                      tipoCanal: canal.type,
                      size: 16,
                      isModoHibrido: canal.isHybridMode,
                    })}
                    <span style={{ paddingTop: 2 }}>{canal.name}</span>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} lg={6}>
              <MuiPickersUtilsProvider locale={ptBR} utils={DateFnsUtils}>
                <KeyboardDatePicker
                  fullWidth
                  disabled={!contatoSelected || isBlocked}
                  disablePast
                  disableToolbar
                  variant="dialog"
                  cancelLabel="Cancelar"
                  inputVariant="outlined"
                  minDate={dayjsService()}
                  showTodayButton={false}
                  format="dd/MM/yyyy"
                  margin="none"
                  id="date-picker-agendar-para-data"
                  label="Selecione a data"
                  value={formik.values.agendarParaDate}
                  onChange={date => {
                    formik.setFieldValue('agendarParaDate', date);
                  }}
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                  error={Boolean(agendarParaDataError)}
                  helperText={agendarParaDataError}
                />
              </MuiPickersUtilsProvider>
            </Grid>

            <Grid item xs={12} lg={6}>
              <MuiPickersUtilsProvider locale={ptBR} utils={DateFnsUtils}>
                <TimePicker
                  fullWidth
                  disabled={!contatoSelected || isBlocked}
                  disableToolbar
                  variant="dialog"
                  cancelLabel="Cancelar"
                  inputVariant="outlined"
                  showTodayButton={false}
                  minutesStep={5}
                  format="HH:mm"
                  ampm={false}
                  margin="none"
                  id="date-picker-agendar-para-hora"
                  label="Selecione a hora"
                  value={formik.values.agendarParaTime}
                  onChange={date => {
                    formik.setFieldValue('agendarParaTime', date);
                  }}
                  error={Boolean(agendarParaHoraError)}
                  helperText={agendarParaHoraError}
                />
              </MuiPickersUtilsProvider>
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="mensagem"
                disabled={!contatoSelected || isBlocked}
                type="text"
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                label="Mensagem"
                variant="outlined"
                placeholder="Mensagem a ser enviada para o contato ao abrir o chamado"
                value={formik.values.mensagem}
                onChange={formik.handleChange}
                error={Boolean(mensagemError)}
                helperText={mensagemError}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="comentario"
                disabled={!contatoSelected || isBlocked}
                type="text"
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                label="Comentario (opcional)"
                variant="outlined"
                placeholder="O comentário de lembrete só aparece para os atendentes"
                value={formik.values.comentario}
                onChange={formik.handleChange}
                error={Boolean(comentarioError)}
                helperText={comentarioError}
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="warning">
                A mensagem <b>não será enviada</b> caso o contato esteja em
                atendimento no dia e horário agendado.
              </Alert>
            </Grid>

            {isBlocked && (
              <Grid item xs={12}>
                <Alert
                  severity="error"
                  style={{
                    backgroundColor: alpha(theme.palette.error.main, 0.12),
                    border: `1px solid ${alpha(
                      theme.palette.error.main,
                      0.05,
                    )}`,
                    color: theme.palette.error.main,
                  }}
                >
                  Contato bloqueado. Não é possível enviar mensagens.
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button
            disabled={
              formik.isSubmitting ||
              !contatoSelected ||
              Boolean(contatoSelected.blockedAt)
            }
            variant="contained"
            color="primary"
            type="submit"
          >
            Pronto
          </Button>
        </DialogActions>
      </DialogForm>
    </Dialog>
  );
}

export default ModalAddAgendamento;
